/* eslint-disable @typescript-eslint/no-explicit-any */
import clone from 'lodash/clone';
import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import sortBy from 'lodash/sortBy';
import toPairs from 'lodash/toPairs';

/**
 * Sort object properties
 *
 * @param object
 * @returns
 */
export function sortObjectProperties<ObjectType extends object>(object: ObjectType) {
  return fromPairs(sortBy(toPairs(object), [0]));
}

/**
 * Check whether the objects are equal
 *
 * @param object1
 * @param object2
 * @returns
 */
export function isObjectEqual<ObjectType extends object>(
  object1: ObjectType,
  object2: ObjectType,
  strict = true,
) {
  const object = clone(object1);
  const other = clone(object2);

  if (strict) return JSON.stringify(object) === JSON.stringify(other);

  return isEqual(sortObjectProperties(object), sortObjectProperties(other));
}

/**
 * Sort object list
 *
 * @param object
 * @param keys
 * @returns
 */
export function sortObjectList<
  ObjectType extends object,
  Key extends keyof ObjectType = keyof ObjectType,
>(object: ObjectType[], keys: Key | Key[] | undefined = undefined, sortProperties = true) {
  const target = sortProperties ? object.map((item) => sortObjectProperties(item)) : clone(object);

  if (keys === undefined) return sortBy(target.map((item) => JSON.stringify(item)));
  return sortBy(object, keys);
}

/**
 * Check whether object list is equal
 *
 * @param a
 * @param b
 * @param key
 * @returns
 */
export function isObjectListEqual<
  ObjectType extends object,
  Key extends keyof ObjectType = keyof ObjectType,
>(object1: ObjectType[], object2: ObjectType[], args?: Key | Key[] | boolean) {
  const object = clone(object1);
  const other = clone(object2);

  if (args === undefined || args === true) return isEqual(object, other);

  if (args === false)
    return isEqual(
      sortObjectList(object, undefined, false),
      sortObjectList(other, undefined, false),
    );

  const castedArgs = typeof args === 'boolean' ? undefined : args;
  return isEqual(sortObjectList(object, castedArgs), sortObjectList(other, castedArgs));
}

/**
 * Check whether the object has valid type based on the passed argument type
 *
 * @param targetObject
 * @param keys
 * @param type
 * @returns
 */
export function isObjectTypeValid<
  ObjectType extends object,
  Key extends keyof ObjectType = keyof ObjectType,
>(
  targetObject: any,
  keys: Key | Key[],
  type: 'some' | 'every' | 'equal' = 'some',
): targetObject is ObjectType {
  if (typeof targetObject !== 'object' || targetObject == null) return false;

  const targetKeys: Key[] = [];
  if (!Array.isArray(keys)) {
    targetKeys.push(keys);
  } else {
    targetKeys.push(...keys);
  }

  switch (type) {
    case 'equal': {
      const targetObjectKeys = Object.keys(targetObject).sort();
      return JSON.stringify(targetKeys.sort()) === JSON.stringify(targetObjectKeys.sort());
    }

    case 'every': {
      return targetKeys.every((key) => key in targetObject);
    }

    case 'some':
    default: {
      return targetKeys.some((key) => key in targetObject);
    }
  }
}

/**
 * Remove null or undefined value
 *
 * @param body
 * @returns
 */
export function cleanObject<T extends object>(body: T) {
  return omitBy(body, isNil);
}

export function getValueFromEnum<EnumType extends string | number | symbol, ValueType>(
  value: EnumType,
  record: Record<EnumType, ValueType>,
  defaultValue?: ValueType,
): ValueType;
export function getValueFromEnum<EnumType extends string | number | symbol, ValueType>(
  value: EnumType | undefined | null,
  record: Record<EnumType, ValueType>,
  defaultValue: ValueType,
): ValueType;
export function getValueFromEnum<EnumType extends string | number | symbol, ValueType>(
  value: EnumType | undefined | null,
  record: Record<EnumType, ValueType>,
  defaultValue?: ValueType,
): ValueType | undefined;
/**
 * Get the value from the enum
 *
 * @param value The enum value
 * @param record The record
 * @returns The value from the enum
 */
export function getValueFromEnum<EnumType extends string | number | symbol, ValueType>(
  value: EnumType | undefined | null,
  record: Record<EnumType, ValueType>,
  defaultValue?: ValueType,
): ValueType {
  if (value == null) return defaultValue as ValueType;

  return record[value];
}
